import { useEffect, useState } from 'react';
import { apiClient } from '../utils/apiClient';

// Polled from the login screen, where nobody is authenticated yet - the
// backend's /api/status is deliberately unauthenticated so this works
// before login. down -> ok is the interesting transition: someone sitting
// on the login screen while the DB comes back shouldn't have to guess that
// a retry will now work, so once we've seen a failure we keep the "back up,
// refresh" banner showing until they actually reload.
const POLL_INTERVAL_MS = 15000;

const SERVICE_LABELS = {
  database: 'Database'
};

const describe = (services) =>
  (services || []).filter((s) => !s.ok).map((s) => SERVICE_LABELS[s.name] || s.name).join(', ') || 'Some services';

function ServiceStatusBanner() {
  const [status, setStatus] = useState('unknown'); // 'unknown' | 'ok' | 'down'
  const [downMessage, setDownMessage] = useState('');
  const [everDown, setEverDown] = useState(false);

  useEffect(() => {
    let cancelled = false;

    const check = async () => {
      try {
        const data = await apiClient.get('/api/status');
        if (cancelled) return;
        if (data.ok) {
          setStatus('ok');
        } else {
          setEverDown(true);
          setStatus('down');
          setDownMessage(describe(data.services));
        }
      } catch (error) {
        if (cancelled) return;
        setEverDown(true);
        setStatus('down');
        setDownMessage('API');
      }
    };

    check();
    const interval = setInterval(check, POLL_INTERVAL_MS);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  if (status === 'down') {
    return (
      <div style={bannerStyle('#f8d7da', '#842029')}>
        ⚠️ {downMessage} currently unavailable — you may not be able to log in right now. We're on it.
      </div>
    );
  }

  if (status === 'ok' && everDown) {
    return (
      <div style={bannerStyle('#d1e7dd', '#0f5132')}>
        ✅ Services are back up.
        <button type="button" onClick={() => window.location.reload()} style={refreshButtonStyle}>
          Refresh
        </button>
      </div>
    );
  }

  return null;
}

const bannerStyle = (background, color) => ({
  position: 'fixed',
  top: 0,
  left: 0,
  right: 0,
  zIndex: 10001,
  padding: '10px 16px',
  textAlign: 'center',
  fontSize: '14px',
  fontWeight: 600,
  backgroundColor: background,
  color
});

const refreshButtonStyle = {
  marginLeft: '12px',
  padding: '3px 12px',
  borderRadius: '4px',
  border: '1px solid #0f5132',
  backgroundColor: 'transparent',
  color: '#0f5132',
  fontWeight: 600,
  cursor: 'pointer'
};

export default ServiceStatusBanner;
